import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Maximize2, Loader2, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function MonthlyReviewsWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchReviewStatus();
  }, []);

  const fetchReviewStatus = async () => {
    try {
      const result = await apiRequest('/monthly-reviews/my-assignments');
      setData(result);
    } catch (error) {
      console.error('Failed to load review status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const pending = data?.pending || [];
  const completed = data?.completed || [];
  const overdue = data?.overdue || [];
  const todayReview = data?.today;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="monthly-reviews-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-violet-600" />
          <h3 className="font-semibold text-slate-900">Monthly Reviews</h3>
          {pending.length > 0 && (
            <Badge className="bg-amber-100 text-amber-700 text-xs">{pending.length} pending</Badge>
          )}
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Today's Review */}
        {todayReview && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Today's Review</span>
            </div>
            <p className="text-sm text-blue-700">{todayReview.employee_name}</p>
            <Button 
              size="sm" 
              className="mt-2 bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate(`/admin/monthly-reviews/review/${todayReview.id}`)}
            >
              Start Review
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-center text-sm">
          <div className="p-2 bg-amber-50 rounded">
            <div className="font-semibold text-amber-700">{pending.length}</div>
            <div className="text-xs text-amber-600">Pending</div>
          </div>
          <div className="p-2 bg-red-50 rounded">
            <div className="font-semibold text-red-700">{overdue.length}</div>
            <div className="text-xs text-red-600">Overdue</div>
          </div>
          <div className="p-2 bg-green-50 rounded">
            <div className="font-semibold text-green-700">{completed.length}</div>
            <div className="text-xs text-green-600">Done</div>
          </div>
        </div>

        {/* Overdue Alert */}
        {overdue.length > 0 && (
          <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-700">
              {overdue.length} overdue review{overdue.length > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* No reviews state */}
        {!todayReview && pending.length === 0 && overdue.length === 0 && (
          <div className="text-center py-2 text-slate-500 text-sm">
            <CheckCircle className="h-6 w-6 mx-auto mb-1 text-green-400" />
            <p>All reviews completed!</p>
          </div>
        )}
      </div>
    </div>
  );
}
